/**
 * CanvasBlockRenderer - Block renderer for canvas editor (play/formation editor)
 *
 * Extends BlockRenderer with canvas-specific behaviors:
 * - CanvasDraggable for grid/node snapping
 * - CanvasSelectable for tool-aware clicks
 */

import React from 'react'
import { getVisualForBlockType } from './Registry'
import { useBlockRenderContext } from './BlockRenderContext'
import { CanvasDraggable, CanvasSelectable } from './behaviors/editor'
import { AnimatedPosition } from './behaviors/animated'
import { BlockErrorBoundary } from './BlockErrorBoundary'
import type { PlayBlock_Union, PlayBlockType } from '@/types/blocks'

interface CanvasBlockRendererProps {
	block: PlayBlock_Union
}

/**
 * Renders a block with canvas-specific behaviors based on render mode
 *
 * - editor: CanvasSelectable + CanvasDraggable wrappers
 * - static: Pure visual, no wrappers
 * - animated: AnimatedPosition wrapper
 */
export function CanvasBlockRenderer({ block }: CanvasBlockRendererProps) {
	const context = useBlockRenderContext()
	const Visual = getVisualForBlockType(block.type as PlayBlockType)

	if (!Visual) {
		console.warn(`No visual component found for block type: ${block.type}`)
		return null
	}

	const visual = <Visual block={block} />
	let content: React.ReactElement

	switch (context.mode) {
		case 'editor':
			content = (
				<CanvasSelectable blockId={String(block.id)}>
					<CanvasDraggable blockId={String(block.id)}>
						{visual}
					</CanvasDraggable>
				</CanvasSelectable>
			)
			break

		case 'static':
			content = visual
			break

		case 'animated':
			content = (
				<AnimatedPosition blockId={String(block.id)} block={block}>
					{visual}
				</AnimatedPosition>
			)
			break

		default: {
			// Exhaustive check: TypeScript enforces all RenderMode values are handled
			const _exhaustiveCheck: never = context.mode
			return _exhaustiveCheck
		}
	}

	return (
		<BlockErrorBoundary blockType={block.type} blockId={String(block.id)}>
			{content}
		</BlockErrorBoundary>
	)
}
